import { zip } from "client_legacy/ndaw/arrayHelpers";
import { isNil, isNotNil } from "../Misc/utils";
import { Navigation } from "./navigation";
import { generateQueryString } from "client_legacy/ndaw/urlHelper";

type NavigationParams =
	| Record<string, string | undefined>
	| undefined;

type HasRouteParamsRouteArgsOptions<TRouteParams extends NavigationParams> = {
	routeParams: TRouteParams;
};

type HasQueryParamsRouteArgsOptions<TQueryParams extends NavigationParams> = {
	queryParams: TQueryParams;
};

type RouteArgsOptions<
	TRouteParams extends NavigationParams,
	TQueryParams extends NavigationParams
> =
	& (TRouteParams extends undefined
		? {}
		: HasRouteParamsRouteArgsOptions<TRouteParams>
	)
	& (TQueryParams extends undefined
		? {}
		: HasQueryParamsRouteArgsOptions<TQueryParams>
	);

type RouteArgs<
	TRouteParams extends NavigationParams,
	TQueryParams extends NavigationParams
> = TRouteParams extends Record<string, string | undefined>
	? TRouteParams | RouteArgsOptions<TRouteParams, TQueryParams>
	: RouteArgsOptions<TRouteParams, TQueryParams>;

export type RouteWithParams<
	TRouteParams extends NavigationParams,
	TQueryParams extends NavigationParams
> = ((args: RouteArgs<TRouteParams, TQueryParams>) => Navigation) & {
	definition: string;
};

type ParameterDefinition<TParameters extends {}> = {
	[Key in StringKeyOf<TParameters>]: undefined extends TParameters[Key]
		? `${Key}?`
		: Key
};

type ParameterExtract<TParameters extends NavigationParams> = TParameters extends {}
	? ParameterDefinition<TParameters>[StringKeyOf<TParameters>]
	: never;

export function route<
	TRouteParams extends NavigationParams = undefined,
	TQueryParams extends NavigationParams = undefined
>(
	strings: TemplateStringsArray,
	...parameterDefinitions: readonly ParameterExtract<TRouteParams>[]
): RouteWithParams<TRouteParams, TQueryParams> {
	function result(params: RouteArgs<TRouteParams, TQueryParams>): Navigation {
		let routeParams: TRouteParams;
		let queryParams: TQueryParams;

		if (isComplex(params as TRouteParams | RouteArgsOptions<TRouteParams, TQueryParams>)) {
			if (hasRouteParams<TRouteParams>(params)) {
				routeParams = params.routeParams;
			} else {
				routeParams = undefined as TRouteParams;
			}
			if (hasQueryParams<TQueryParams>(params)) {
				queryParams = params.queryParams;
			} else {
				queryParams = undefined as TQueryParams;
			}
		} else {
			routeParams = params as TRouteParams;
			queryParams = undefined as TQueryParams;
		}

		const path: string = normalizePath(
			buildUrl<TRouteParams>(
				strings,
				parameterDefinitions,
				routeParams
			)
		);

		let queryString: string | undefined = undefined;

		if (isNotNil(queryParams)) {
			queryString = generateQueryString(queryParams as Record<string, string | undefined>);
		}

		return {
			path,
			queryString
		};
	}

	result.definition = zip(strings.slice(0, strings.length - 1), parameterDefinitions)
		.map(([template, param]) => `${template}:${param}`)
		.join("") + strings[strings.length - 1];

	return result;
}

function buildUrl<TParams extends Record<string, string | undefined> | undefined>(
	strings: TemplateStringsArray,
	parameterDefinitions: readonly ParameterExtract<TParams>[],
	params: TParams
): string {
	let result: string = "";
	let shouldResultBeLocked: boolean = false;

	const appendToResult = (value: string): void => {
		if (!shouldResultBeLocked) {
			result += value;
		} else if (value !== "" && value !== "/") {
			throw new Error("Cannot insert anything after an empty optional parameter.");
		}
	};

	for (let i = 0; i < strings.length; i++) {
		const template: string = strings[i];
		const paramDefinition: ParameterExtract<TParams> | undefined = parameterDefinitions[i];

		appendToResult(template);

		if (isNil(paramDefinition)) {
			if (i === strings.length - 1) {
				return result;
			} else {
				throw new Error("Mismatch between number of parameters and string templates");
			}
		}

		if (paramDefinition === "") {
			throw new Error("Parameter definition cannot be an empty string");
		}

		let propertyName: StringKeyOf<TParams>;
		let isOptional: boolean;

		if (paramDefinition.endsWith("?")) {
			propertyName = paramDefinition.slice(0, paramDefinition.length - 1) as StringKeyOf<TParams>;
			isOptional = true;
		} else {
			propertyName = paramDefinition as StringKeyOf<TParams>;
			isOptional = false;
		}

		const paramValue: string | undefined = params?.[propertyName];

		if (isOptional) {
			if (!paramValue) {
				shouldResultBeLocked = true;
			} else {
				appendToResult(encodeURIComponent(paramValue));
			}
		} else {
			if (!paramValue) {
				throw new Error(`Required parameter ${propertyName} is not set`);
			} else {
				appendToResult(encodeURIComponent(paramValue));
			}
		}
	}

	return result;
}

function normalizePath(route: string): string {
	const withoutTrailingSlashes: string = route.replace(/\/+$/, "");

	// The root route must stay "/" rather than collapse to "", because react-router treats an empty
	// pathname as "keep the current route" and the navigation silently does nothing.
	return withoutTrailingSlashes === ""
		? "/"
		: withoutTrailingSlashes;
}

function isComplex<
	TRouteParams extends NavigationParams,
	TQueryParams extends NavigationParams
>(args: TRouteParams | RouteArgsOptions<TRouteParams, TQueryParams>): args is RouteArgsOptions<TRouteParams, TQueryParams> {
	return args !== undefined
		&& (
			hasRouteParams(args) || hasQueryParams(args)
		);
}

function hasRouteParams<
	TRouteParams extends NavigationParams
>(args: HasRouteParamsRouteArgsOptions<TRouteParams> | Record<string, any>): args is HasRouteParamsRouteArgsOptions<TRouteParams> {
	return "routeParams" in args
		&& typeof args.routeParams === "object"
		&& args.routeParams !== null;
}

function hasQueryParams<
	TQueryParams extends NavigationParams
>(args: HasQueryParamsRouteArgsOptions<TQueryParams> | Record<string, any>): args is HasQueryParamsRouteArgsOptions<TQueryParams> {
	return "queryParams" in args
		&& typeof args.queryParams === "object"
		&& args.queryParams !== null;
}